import * as React from 'react';
import { X } from '@phosphor-icons/react';
import { useCampaignCapabilitiesStore } from '@discovr/features';
import { ImportProgressBar } from './ImportProgressBar';
import { ImportResultsPanel } from './ImportResultsPanel';

/**
 * Non-blocking, dismissible creator-import surface pinned to the corner of the
 * campaign page. Mounted once on CampaignDetailPage so it stays visible across
 * every tab while an import runs — a viewer can keep scrolling the rest of the
 * campaign. Replaces the old auto-opening blocking ImportStatusModal.
 *
 * Admin-side only: rendered for `lane === 'admin'` viewers, and suppressed while
 * the uploader's own import wizard is open (that flow shows progress inline).
 * The brand side never receives import SSE, so it never shows here.
 */
export function ImportProgressPopup() {
  const isAdminLane = useCampaignCapabilitiesStore((s) => s.lane === 'admin');
  const importProgress = useCampaignCapabilitiesStore((s) => s.importProgress);
  const importResult = useCampaignCapabilitiesStore((s) => s.importResult);
  const wizardOpen = useCampaignCapabilitiesStore((s) => s.importWizardOpen);
  const setImportProgress = useCampaignCapabilitiesStore((s) => s.setImportProgress);
  const setImportResult = useCampaignCapabilitiesStore((s) => s.setImportResult);

  const running = !!importProgress && !importResult;

  // Sticky dismiss for a RUNNING import: the BE keeps emitting progress ticks,
  // so nulling the store wouldn't stick (the next tick re-shows it). Keep a
  // local "this run is dismissed" flag, re-armed when the run ends — using
  // React's adjust-state-on-change-during-render pattern (not an effect, not a
  // ref, so it satisfies the react-hooks lint rules).
  const [runDismissed, setRunDismissed] = React.useState(false);
  const [prevRunning, setPrevRunning] = React.useState(running);
  if (running !== prevRunning) {
    setPrevRunning(running);
    if (!running) setRunDismissed(false);
  }

  const dismiss = React.useCallback(() => {
    if (importProgress && !importResult) {
      setRunDismissed(true); // running: more ticks coming — suppress this run
    } else {
      setImportResult(null); // completed result: no more ticks, clearing sticks
      setImportProgress(null);
    }
  }, [importProgress, importResult, setImportProgress, setImportResult]);

  const showRunning = running && !runDismissed;

  // Brand lane never receives import SSE — render nothing (no empty live
  // region left in the brand accessibility tree).
  if (!isAdminLane) return null;

  const visible = !wizardOpen && (showRunning || !!importResult);

  // Announce only meaningful transitions — start and completion — never the
  // per-tick progress count. The whole popup used to sit inside one aria-live
  // region, so every import.progress SSE tick queued a fresh screen-reader
  // announcement. The running count already lives in ImportProgressBar's
  // role="progressbar" (exposed via aria-valuenow), so the polite region below
  // only needs to change state, not echo the numbers. The message text is kept
  // distinct from the visible "Importing creators…" so it reads as a status,
  // not a duplicate.
  const announcement = !visible ? '' : importResult ? 'Creator import complete.' : 'Creator import in progress.';

  return (
    <div className="fixed bottom-6 right-6 z-50">
      <div role="status" aria-live="polite" className="sr-only">
        {announcement}
      </div>
      {visible && (
        <div className="relative w-[400px] max-w-[calc(100vw-2rem)]">
          <div className="max-h-[70vh] overflow-y-auto rounded-xl shadow-xl">
            {showRunning && importProgress ? (
              <ImportProgressBar {...importProgress} />
            ) : importResult ? (
              <ImportResultsPanel result={importResult} onReset={dismiss} />
            ) : null}
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss import"
            className="absolute -right-2 -top-2 rounded-full border border-border bg-card p-1 text-muted-foreground shadow-sm hover:text-foreground"
          >
            <X aria-hidden="true" className="size-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
